/**
 * Reimbursement Analytics Module
 * Tracks user behavior in the reimbursement flow for Datadog RUM
 *
 * Events tracked:
 * - reimbursement_flow_started: When user enters the reimbursement selection page
 * - reimbursement_type_selected: When user clicks on a reimbursement type
 * - reimbursement_payment_method_selected: When user selects a payment method
 * - reimbursement_upload_started: When user begins document upload
 * - reimbursement_upload_completed: When document upload succeeds
 * - reimbursement_submitted: When reimbursement is fully submitted
 * - reimbursement_abandoned: When user leaves without completing
 */

import { ReimbursementType } from './upload/upload-types';

declare global {
    interface Window {
        datadogRum?: {
            addAction: (
                name: string,
                context?: Record<string, unknown>
            ) => void;
        };
    }
}

// Track the current reimbursement flow state
interface IReimbursementFlowState {
    type: ReimbursementType | null;
    step:
        'select_type' | 'payment_method' | 'upload' | 'submitted' | 'completed';
    startTime: number;
    paymentMethod: string | null;
}

// Session storage key for flow state
const FLOW_STATE_KEY = 'reimbursement_flow_state';

/**
 * Get the current flow state from session storage
 */
function getFlowState(): IReimbursementFlowState | null {
    try {
        const stored = sessionStorage.getItem(FLOW_STATE_KEY);
        return stored ? (JSON.parse(stored) as IReimbursementFlowState) : null;
    } catch {
        return null;
    }
}

/**
 * Save flow state to session storage
 */
function setFlowState(state: IReimbursementFlowState): void {
    try {
        sessionStorage.setItem(FLOW_STATE_KEY, JSON.stringify(state));
    } catch {
        // Session storage not available
    }
}

/**
 * Clear flow state
 */
function clearFlowState(): void {
    try {
        sessionStorage.removeItem(FLOW_STATE_KEY);
    } catch {
        // Session storage not available
    }
}

/**
 * Send a RUM action if Datadog RUM is available
 */
function trackAction(
    name: string,
    context: Record<string, unknown> = {}
): void {
    if (window.datadogRum) {
        window.datadogRum.addAction(name, context);
    }
}

/**
 * Track when user enters the reimbursement selection page
 * Call this on the select-type page load
 */
export function trackReimbursementFlowStarted(): void {
    const state: IReimbursementFlowState = {
        type: null,
        step: 'select_type',
        startTime: Date.now(),
        paymentMethod: null
    };
    setFlowState(state);

    trackAction('reimbursement_flow_started', {
        timestamp: new Date().toISOString()
    });
}

/**
 * Track when user selects a reimbursement type
 */
export function trackReimbursementTypeSelected(type: ReimbursementType): void {
    const state = getFlowState() || {
        type: null,
        step: 'select_type' as const,
        startTime: Date.now(),
        paymentMethod: null
    };

    state.type = type;
    state.step = 'payment_method';
    setFlowState(state);

    trackAction('reimbursement_type_selected', {
        reimbursement_type: type,
        time_on_select_page_ms: Date.now() - state.startTime
    });
}

/**
 * Track when user selects a payment method
 */
export function trackPaymentMethodSelected(
    type: ReimbursementType,
    paymentMethod: string
): void {
    const state = getFlowState();
    if (state) {
        state.step = 'upload';
        state.paymentMethod = paymentMethod;
        setFlowState(state);
    }

    trackAction('reimbursement_payment_method_selected', {
        reimbursement_type: type,
        payment_method: paymentMethod
    });
}

/**
 * Track when user starts uploading a document
 */
export function trackUploadStarted(
    type: ReimbursementType,
    uploadType: string
): void {
    trackAction('reimbursement_upload_started', {
        reimbursement_type: type,
        upload_type: uploadType
    });
}

/**
 * Track when document upload completes successfully
 */
export function trackUploadCompleted(
    type: ReimbursementType,
    uploadType: string
): void {
    trackAction('reimbursement_upload_completed', {
        reimbursement_type: type,
        upload_type: uploadType
    });
}

/**
 * Track when reimbursement is fully submitted
 */
export function trackReimbursementSubmitted(type: ReimbursementType): void {
    const state = getFlowState();
    const timeToSubmit = state ? Date.now() - state.startTime : undefined;

    trackAction('reimbursement_submitted', {
        reimbursement_type: type,
        payment_method: state?.paymentMethod ?? 'unknown',
        time_to_submit_ms: timeToSubmit
    });

    // Mark as submitted so abandonment isn't tracked
    if (state) {
        state.step = 'submitted';
        setFlowState(state);
    }

    // Clear state after short delay to allow the action to be sent
    setTimeout(() => clearFlowState(), 1000);
}

/**
 * Track when user abandons the reimbursement flow
 * This should be called on page unload if flow was not completed
 */
export function trackReimbursementAbandoned(): void {
    const state = getFlowState();

    // Only track if there was an active flow that wasn't submitted
    if (state && state.step !== 'submitted' && state.step !== 'completed') {
        trackAction('reimbursement_abandoned', {
            reimbursement_type: state.type ?? 'unknown',
            abandoned_at_step: state.step,
            payment_method: state.paymentMethod ?? 'not_selected',
            time_in_flow_ms: Date.now() - state.startTime
        });

        clearFlowState();
    }
}

/**
 * Initialize abandonment tracking
 * Call this once on reimbursement pages to track when users leave
 */
export function initAbandonmentTracking(): void {
    // Use pagehide for better mobile support
    window.addEventListener('pagehide', () => {
        trackReimbursementAbandoned();
    });

    // Fallback for browsers that don't support pagehide well
    window.addEventListener('beforeunload', () => {
        trackReimbursementAbandoned();
    });
}

/**
 * Initialize analytics on the select-type page
 */
export function initSelectTypePage(): void {
    // Track flow started
    trackReimbursementFlowStarted();

    // Set up abandonment tracking
    initAbandonmentTracking();

    // Track type selection when user clicks on a reimbursement card
    document
        .querySelectorAll('a[href*="/reimbursements/payment-method"]')
        .forEach(link => {
            link.addEventListener('click', () => {
                const href = link.getAttribute('href') ?? '';
                const typeMatch = href.match(/reimbursement_type=(\w+)/);
                if (typeMatch) {
                    trackReimbursementTypeSelected(
                        typeMatch[1] as ReimbursementType
                    );
                }
            });
        });
}

/**
 * Initialize analytics on the payment-method page
 */
export function initPaymentMethodPage(): void {
    // Set up abandonment tracking
    initAbandonmentTracking();

    // Get reimbursement type from URL
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('reimbursement_type') as ReimbursementType;

    // Track payment method selection
    document
        .querySelectorAll('a[href*="/reimbursements/upload"]')
        .forEach(link => {
            link.addEventListener('click', () => {
                const href = link.getAttribute('href') ?? '';
                const tokenMatch = href.match(/payment_token=([^&]+)/);
                if (tokenMatch && type) {
                    trackPaymentMethodSelected(type, tokenMatch[1]);
                }
            });
        });
}

/**
 * Initialize analytics on the upload page
 */
export function initUploadPage(): void {
    // Set up abandonment tracking
    initAbandonmentTracking();
}

// Auto-initialize based on current page
(function initReimbursementAnalytics(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOnPageLoad);
    } else {
        initOnPageLoad();
    }

    function initOnPageLoad(): void {
        const path = window.location.pathname;

        if (
            path === '/reimbursements/select-type' ||
            path === '/reimbursements'
        ) {
            initSelectTypePage();
        } else if (path === '/reimbursements/payment-method') {
            initPaymentMethodPage();
        } else if (path.startsWith('/reimbursements/upload')) {
            initUploadPage();
        }
    }
})();
